import { ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

export default function PrivacyPolicy() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-dark-50">
      {/* Header */}
      <header className="bg-dark-100 border-b border-dark-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-vapour-600 hover:text-vapour-900 mr-3"
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-xl font-semibold text-vapour-900">Privacy Policy</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-dark-200 p-8">
          
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-vapour-900 mb-4">Privacy Policy</h1>
            <p className="text-vapour-600 text-lg">
              Effective Date: September 4, 2025 | Last Updated: September 4, 2025
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">1. Introduction</h2>
              <p className="text-vapour-700 mb-4">
                Welcome to VapourVape Rewards ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our loyalty program application and services.
              </p>
              <p className="text-vapour-700">
                By using our services, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
              </p>
            </section>

            {/* Information We Collect */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">2.1 Personal Information</h3>
              <p className="text-vapour-700 mb-4">We may collect the following personal information:</p>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li><strong>Account Information:</strong> Name, email address, phone number</li>
                <li><strong>Authentication Data:</strong> Login credentials, authentication tokens</li>
                <li><strong>Profile Information:</strong> Preferences, marketing consent status</li>
                <li><strong>Loyalty Program Data:</strong> Stamp count, reward history, redemption records</li>
                <li><strong>Transaction Information:</strong> Purchase history, visit frequency</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">2.2 Technical Information</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li><strong>Device Information:</strong> Device type, operating system, browser type</li>
                <li><strong>Usage Data:</strong> App interactions, feature usage, session duration</li>
                <li><strong>Location Data:</strong> General location for service delivery (with consent)</li>
                <li><strong>Camera Access:</strong> QR code scanning functionality (processed locally)</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">2.3 Automatically Collected Information</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>IP addresses and connection information</li>
                <li>Log files and error reports</li>
                <li>Analytics and performance data</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            {/* How We Use Information */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">3. How We Use Your Information</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">3.1 Primary Purposes</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li><strong>Service Delivery:</strong> Operate and maintain the loyalty program</li>
                <li><strong>Account Management:</strong> Create and manage your account</li>
                <li><strong>Reward Processing:</strong> Track stamps, process redemptions</li>
                <li><strong>Customer Support:</strong> Respond to inquiries and provide assistance</li>
                <li><strong>Authentication:</strong> Verify identity and secure accounts</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">3.2 Secondary Purposes</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li><strong>Marketing Communications:</strong> Send promotional offers (with consent)</li>
                <li><strong>Service Improvement:</strong> Analyze usage patterns and enhance features</li>
                <li><strong>Personalization:</strong> Customize user experience and recommendations</li>
                <li><strong>Analytics:</strong> Generate insights for business optimization</li>
                <li><strong>Legal Compliance:</strong> Meet regulatory and legal requirements</li>
              </ul>
            </section>

            {/* Information Sharing */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">4. Information Sharing and Disclosure</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">4.1 We Do Not Sell Personal Information</h3>
              <p className="text-vapour-700 mb-4">
                We do not sell, rent, or trade your personal information to third parties for monetary consideration.
              </p>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">4.2 Limited Sharing</h3>
              <p className="text-vapour-700 mb-4">We may share information in the following circumstances:</p>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li><strong>Service Providers:</strong> Third-party vendors who assist in service delivery</li>
                <li><strong>Business Transfers:</strong> In case of merger, acquisition, or asset sale</li>
                <li><strong>Legal Requirements:</strong> When required by law or legal process</li>
                <li><strong>Safety and Security:</strong> To protect rights, property, or safety</li>
                <li><strong>Consent:</strong> When you explicitly authorize sharing</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">4.3 Third-Party Services</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li><strong>Firebase/Google Cloud:</strong> Authentication and data storage</li>
                <li><strong>Google Wallet:</strong> Digital pass creation (optional)</li>
                <li><strong>Analytics Providers:</strong> Usage analytics and performance monitoring</li>
                <li><strong>Email Services:</strong> Transactional and marketing communications</li>
              </ul>
            </section>

            {/* Data Security */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">5. Data Security</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">5.1 Security Measures</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li><strong>Encryption:</strong> Data encrypted in transit and at rest</li>
                <li><strong>Access Controls:</strong> Role-based access and authentication</li>
                <li><strong>Secure Infrastructure:</strong> Cloud-based security with Google Firebase</li>
                <li><strong>Regular Audits:</strong> Security assessments and vulnerability testing</li>
                <li><strong>Staff Training:</strong> Employee education on data protection</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">5.2 Data Breach Response</h3>
              <p className="text-vapour-700 mb-4">
                In the event of a data breach, we will notify affected users within 72 hours and take immediate steps to secure the system and prevent further unauthorized access.
              </p>
            </section>

            {/* Your Rights */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">6. Your Privacy Rights</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">6.1 Access and Control</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li><strong>Access:</strong> Request copies of your personal information</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal data</li>
                <li><strong>Portability:</strong> Receive your data in a portable format</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">6.2 Marketing Preferences</h3>
              <p className="text-vapour-700 mb-4">
                You can opt out of marketing communications at any time by:
              </p>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>Clicking unsubscribe links in emails</li>
                <li>Updating preferences in your account settings</li>
                <li>Contacting us directly at support@vapourvape.com</li>
              </ul>
            </section>

            {/* Data Retention */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">7. Data Retention</h2>
              <p className="text-vapour-700 mb-4">
                We retain personal information for as long as necessary to:
              </p>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>Provide our services and maintain your account</li>
                <li>Comply with legal obligations and resolve disputes</li>
                <li>Maintain business records for operational purposes</li>
                <li>Enforce our agreements and protect our rights</li>
              </ul>
              <p className="text-vapour-700">
                Inactive accounts may be deleted after 3 years of inactivity, with 30 days advance notice.
              </p>
            </section>

            {/* Cookies and Tracking */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">8. Cookies and Tracking Technologies</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">8.1 Types of Cookies</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li><strong>Essential Cookies:</strong> Required for basic functionality</li>
                <li><strong>Performance Cookies:</strong> Analytics and performance monitoring</li>
                <li><strong>Functional Cookies:</strong> Remember preferences and settings</li>
                <li><strong>Marketing Cookies:</strong> Personalized advertising (with consent)</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">8.2 Managing Cookies</h3>
              <p className="text-vapour-700">
                You can control cookies through your browser settings. Note that disabling certain cookies may affect functionality.
              </p>
            </section>

            {/* Children's Privacy */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">9. Children's Privacy</h2>
              <p className="text-vapour-700 mb-4">
                Our services are not intended for children under 18 years of age. We do not knowingly collect personal information from children under 18. If we become aware that we have collected personal information from a child under 18, we will take steps to delete such information.
              </p>
              <p className="text-vapour-700">
                If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
              </p>
            </section>

            {/* International Transfers */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">10. International Data Transfers</h2>
              <p className="text-vapour-700 mb-4">
                Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your personal information in accordance with applicable data protection laws.
              </p>
            </section>

            {/* Changes to Policy */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">11. Changes to This Privacy Policy</h2>
              <p className="text-vapour-700 mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by:
              </p>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>Posting the updated policy on our website</li>
                <li>Sending email notifications to registered users</li>
                <li>Displaying in-app notifications</li>
              </ul>
              <p className="text-vapour-700">
                Your continued use of our services after changes become effective constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* Contact Information */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">12. Contact Us</h2>
              <p className="text-vapour-700 mb-4">
                If you have questions about this Privacy Policy or our privacy practices, please contact us:
              </p>
              
              <div className="bg-vapour-50 border border-vapour-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-vapour-900 mb-3">VapourVape Privacy Team</h3>
                <div className="space-y-2 text-vapour-700">
                  <p><strong>Email:</strong> privacy@vapourvape.com</p>
                  <p><strong>Support:</strong> support@vapourvape.com</p>
                  <p><strong>Phone:</strong> +1234567890</p>
                  <p><strong>Address:</strong> [Your Business Address]</p>
                </div>
                
                <div className="mt-4 pt-4 border-t border-vapour-200">
                  <p className="text-sm text-vapour-600">
                    <strong>Response Time:</strong> We aim to respond to privacy inquiries within 30 days.
                  </p>
                </div>
              </div>
            </section>

            {/* Legal Basis */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">13. Legal Basis for Processing (GDPR)</h2>
              <p className="text-vapour-700 mb-4">
                For users in the European Union, we process personal data based on:
              </p>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li><strong>Contract Performance:</strong> To provide loyalty program services</li>
                <li><strong>Legitimate Interest:</strong> For analytics and service improvement</li>
                <li><strong>Consent:</strong> For marketing communications and optional features</li>
                <li><strong>Legal Obligation:</strong> To comply with applicable laws</li>
              </ul>
            </section>

          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-dark-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="text-sm text-vapour-600">
                <p>Last updated: September 4, 2025</p>
                <p>Version 1.0</p>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => navigate('/terms')}
                  className="text-vapour-600 hover:text-vapour-900 text-sm underline"
                >
                  Terms of Service
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="text-vapour-600 hover:text-vapour-900 text-sm underline"
                >
                  Back to Home
                </button>
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  )
}